// src/components/Eventos/EventsHeats/utils/fieldPositionTracker.js
import { isSpecial } from './eventHelpers';

/**
 * Calcula las posiciones de los atletas en cada intento de un evento de campo
 * @param {Array} athletes - Array de atletas con sus intentos
 * @param {number} maxAttempts - Número máximo de intentos
 * @returns {Object} - Objeto con posiciones y cambios por atleta e intento
 */
export const computeFieldPositions = (athletes, maxAttempts) => {
  const result = {};
  
  // Crear estructura para almacenar resultados por intento
  const attemptRankings = [];
  
  // Procesar cada intento
  for (let attemptIndex = 0; attemptIndex < maxAttempts; attemptIndex++) {
    const attemptResults = [];
    
    // Recopilar todos los resultados válidos hasta este intento (incluido)
    athletes.forEach(athlete => {
      const { number, attemptsData = [] } = athlete;
      
      // Obtener la mejor marca hasta este intento (incluido)
      let bestResult = null;
      let bestValue = 0;
      let hasAnyAttempt = false; // NUEVO: rastrear si tiene algún intento registrado
      
      for (let i = 0; i <= attemptIndex && i < attemptsData.length; i++) {
        const attempt = attemptsData[i];
        
        // CORREGIDO: verificar si tiene algún intento registrado (incluye fauls)
        if (attempt && attempt.result && attempt.result !== '-' && attempt.result !== '') {
          hasAnyAttempt = true;
          
          // Solo considerar para ranking si NO es especial
          if (!isSpecial(attempt.result)) {
            const value = parseFloat(attempt.result);
            if (!isNaN(value) && value > bestValue) {
              bestValue = value;
              bestResult = attempt.result;
            }
          }
        }
      }
      
      // CORREGIDO: incluir en ranking solo si tiene resultado válido
      // pero mantener en la estructura si tiene algún intento registrado
      if (bestResult && bestValue > 0) {
        attemptResults.push({
          number,
          result: bestResult,
          value: bestValue,
          attemptIndex,
          hasValidResult: true
        });
      } else if (hasAnyAttempt) {
        // NUEVO: incluir atletas con solo intentos especiales (fauls)
        attemptResults.push({
          number,
          result: null,
          value: 0,
          attemptIndex,
          hasValidResult: false
        });
      }
    });
    
    // Ordenar por mejor resultado (mayor distancia)
    // Los que no tienen resultado válido van al final
    attemptResults.sort((a, b) => {
      if (a.hasValidResult && !b.hasValidResult) return -1;
      if (!a.hasValidResult && b.hasValidResult) return 1;
      if (!a.hasValidResult && !b.hasValidResult) return 0;
      return b.value - a.value;
    });
    
    // Asignar posiciones
    const ranking = {};
    let currentPosition = 1;
    
    attemptResults.forEach((athlete) => {
      ranking[athlete.number] = {
        position: athlete.hasValidResult ? currentPosition : null, // null para atletas sin resultado válido
        result: athlete.result,
        value: athlete.value,
        hasValidResult: athlete.hasValidResult
      };
      
      if (athlete.hasValidResult) {
        currentPosition++;
      }
    });
    
    attemptRankings.push(ranking);
  }
  
  // Calcular cambios de posición para cada atleta
  athletes.forEach(athlete => {
    const { number } = athlete;
    result[number] = {
      positions: [],
      changes: [],
      results: []
    };
    
    for (let i = 0; i < maxAttempts; i++) {
      const currentRanking = attemptRankings[i];
      const previousRanking = i > 0 ? attemptRankings[i - 1] : null;
      
      const currentPos = currentRanking[number]?.position || null;
      const previousPos = previousRanking?.[number]?.position || null;
      
      result[number].positions.push(currentPos);
      result[number].results.push(currentRanking[number]?.result || null);
      
      // Calcular cambio de posición
      let change = null;
      if (currentPos && previousPos) {
        const diff = previousPos - currentPos;
        if (diff > 0) {
          change = { dir: 'up', val: diff };
        } else if (diff < 0) {
          change = { dir: 'down', val: Math.abs(diff) };
        }
      }
      
      result[number].changes.push(change);
    }
  });
  
  return result;
};

/**
 * Obtiene las estadísticas de posición para un atleta específico
 * @param {Object} positionData - Datos de posición calculados
 * @param {string} athleteNumber - Número del atleta
 * @returns {Object} - Estadísticas de posición del atleta
 */
export const getAthletePositionStats = (positionData, athleteNumber) => {
  const athleteData = positionData[athleteNumber];
  if (!athleteData) return null;
  
  const validPositions = athleteData.positions.filter(pos => pos !== null);
  if (validPositions.length === 0) return null;
  
  return {
    bestPosition: Math.min(...validPositions),
    worstPosition: Math.max(...validPositions),
    finalPosition: validPositions[validPositions.length - 1],
    totalChanges: athleteData.changes.filter(change => change !== null).length,
    positions: athleteData.positions,
    changes: athleteData.changes,
    results: athleteData.results
  };
};

/**
 * Formatea los datos de intentos con información de posición
 * @param {Array} attemptsData - Datos originales de intentos
 * @param {Object} positionStats - Estadísticas de posición del atleta
 * @returns {Array} - Intentos formateados con datos de posición
 */
export const formatAttemptsWithPositions = (attemptsData, positionStats) => {
  if (!attemptsData) return [];
  
  // CORREGIDO: usar attemptsData directamente, no depender de positionStats
  return attemptsData.map((attempt, index) => ({
    ...attempt,
    position: positionStats?.positions[index] || null,
    positionChange: positionStats?.changes[index] || null,
    attemptNumber: index + 1,
    isValidAttempt: attempt && attempt.result && attempt.result !== '-' && attempt.result !== '' && !isSpecial(attempt.result),
    // NUEVO: flag para indicar si es un faul
    isFaul: attempt && attempt.result === 'X',
    // NUEVO: flag para indicar si tiene algún resultado registrado
    hasResult: attempt && attempt.result && attempt.result !== '-' && attempt.result !== ''
  }));
};